import { DestroyRef, Directive, Injectable, NgModule, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { CdsLangService } from '@cds/ng-core/lang';
import { CDS_DATE_FORMATS, CdsDatepickerComponent, CdsDatepickerModule, DateAdapter } from '@cds/ng-web-components/datepicker';
import { skip } from 'rxjs';
import { MY_FORMATS } from 'src/app/config/datePicker';

type DateNameStyle = 'long' | 'short' | 'narrow';

@Injectable()
class LocalizedMomentDateAdapter extends MomentDateAdapter {
  private cdsLangService = inject(CdsLangService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.cdsLangService.currentLanguageObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      this.setLocale(lang === 'en' ? 'en-GB' : 'zh-HK');
    });
  }

  override getMonthNames(style: DateNameStyle): string[] {
    if (!this.isChineseLocale) {
      return super.getMonthNames(style);
    }

    const formatter = new Intl.DateTimeFormat('zh-HK', { month: style, timeZone: 'UTC' });
    return Array.from({ length: 12 }, (_, month) => formatter.format(new Date(Date.UTC(2020, month, 1))));
  }

  override getDayOfWeekNames(style: DateNameStyle): string[] {
    if (!this.isChineseLocale) {
      return super.getDayOfWeekNames(style);
    }

    const formatter = new Intl.DateTimeFormat('zh-HK', { weekday: style, timeZone: 'UTC' });
    return Array.from({ length: 7 }, (_, day) => formatter.format(new Date(Date.UTC(2020, 5, 7 + day))));
  }

  override getFirstDayOfWeek(): number {
    return this.isChineseLocale ? 0 : super.getFirstDayOfWeek();
  }

  private get isChineseLocale(): boolean {
    return String(this.locale).toLowerCase().startsWith('zh');
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[cdsDatepicker],cds-datepicker',
  providers: [{ provide: DateAdapter, useClass: LocalizedMomentDateAdapter }],
})
export class CdsDatepickerLocaleScopeDirective {
  private datepicker = inject(CdsDatepickerComponent, { self: true, optional: true });
  private cdsLangService = inject(CdsLangService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    if (this.datepicker) {
      this.cdsLangService.currentLanguageObservable.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.datepicker?.opened) {
          this.datepicker.close();
          this.datepicker.open();
        }
      });
    }
  }
}

@NgModule({
  imports: [CdsDatepickerModule, CdsDatepickerLocaleScopeDirective],
  exports: [CdsDatepickerModule, CdsDatepickerLocaleScopeDirective],
  providers: [
    { provide: DateAdapter, useClass: LocalizedMomentDateAdapter },
    { provide: CDS_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class AppCdsDatepickerModule {}
